/**
 * AMS Layout with Sidebar Navigation
 * Collapsible sidebar with hover expansion and session-based state
 */
import { Outlet, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { TopBar } from "./TopBar";
import { useAuth } from "@/global/auth/AuthContext";
import { useTour } from "@/global/contexts/TourContext";

// Whitelisted test accounts - always show tour on dashboard
const TOUR_WHITELIST_EMAILS = [
  "test@orcaams.com",
  "demo@orcaams.com",
  // Add more test accounts here
];

export function Layout() {
  const location = useLocation();
  const { user, loading } = useAuth();
  const { startTour } = useTour();

  // Trigger tours for first-time users or whitelisted accounts
  useEffect(() => {
    console.log('[Layout] Tour check:', {
      loading,
      hasUser: !!user,
      pathname: location.pathname,
    });
    
    // Don't run tour logic if auth is still loading or user is not authenticated
    if (loading || !user) {
      console.log('[Layout] Skipping tour - auth not ready');
      return;
    }

    const hasCompletedTour = user.user_metadata?.has_completed_ams_tour;
    const isWhitelisted = user.email && TOUR_WHITELIST_EMAILS.includes(user.email);
    
    console.log('[Layout] Tour state:', {
      hasCompletedTour,
      isWhitelisted,
      email: user.email,
    });
    
    // Trigger tour if:
    // 1. User hasn't completed it yet, OR
    // 2. User is whitelisted (for testing)
    const shouldShowTour = !hasCompletedTour || isWhitelisted;
    
    if (!shouldShowTour) {
      return;
    }

    // Map paths to tour IDs
    let tourId: "ams-main" | "ams-agents" | "ams-agencies" | null = null;
    
    if (location.pathname === "/dashboard") {
      tourId = "ams-main";
    } else if (location.pathname === "/agents") {
      tourId = "ams-agents";
    } else if (location.pathname === "/agencies") {
      tourId = "ams-agencies";
    }
    
    if (tourId) {
      console.log(`[Layout] Starting ${tourId} tour in 1 second...`);
      // Delay tour start to allow page to render
      const timer = setTimeout(() => {
        console.log(`[Layout] Starting ${tourId} tour now`);
        startTour(tourId);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [user, loading, location.pathname, startTour]);

  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar data-tour="sidebar" />
      <SidebarInset className="relative">
        <TopBar />
        <main
          className="flex flex-1 flex-col gap-4 p-4 w-full max-w-full overflow-x-hidden"
          style={{ paddingTop: "calc(80px + env(safe-area-inset-top))" }}
        >
          <Suspense
            fallback={<div className="text-muted-foreground">Loading...</div>}
          >
            <Outlet />
          </Suspense>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
